import { Toast } from 'jamespot-front-business';
import { useAbortController } from 'jamespot-react-components';
import jApi, {
    ImapConnection,
    OfficeProvider,
    OfficeSetConfigurationPayload,
    ValueOf,
    WidgetDisplayMode,
    WidgetEmailsImapType,
} from 'jamespot-user-api';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useAppDispatch } from '../../../../redux/store';
import { commonComponentsRegistry } from '../../../../registry/CommonRegistry';

const ModalLayout = commonComponentsRegistry.getLazyComponent('ModalLayout');
const ModalContent = commonComponentsRegistry.getLazyComponent('ModalContent');
const Button = commonComponentsRegistry.getLazyComponent('Button');
const InputText = commonComponentsRegistry.getLazyComponent('InputText');
const InputNumber = commonComponentsRegistry.getLazyComponent('InputNumber');
const InputSelectExtended = commonComponentsRegistry.getLazyComponent('InputSelectExtended');
const InputPassword = commonComponentsRegistry.getLazyComponent('InputPassword');
const Typography = commonComponentsRegistry.getLazyComponent('Typography');
const JRCLoader = commonComponentsRegistry.getLazyComponent('Loader');

export const UIForm = styled.div<{ isLocked: boolean }>`
    display: flex;
    flex-direction: column;
    opacity: ${(props) => (props.isLocked ? '.4' : '1.0')};
`;

export const UIWrapper = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const UIProviderError = styled.div`
    background-color: ${(props) => props.theme.color.orange};
    color: ${(props) => props.theme.color.white};
    padding: ${(props) => props.theme.space.sm}px;
    border-radius: ${(props) => props.theme.space.sm}px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const UISection = styled.div`
    margin-bottom: ${(props) => props.theme.space.md}px;
`;

export type JRCWidgetEmailsImapProps = {
    widget: WidgetEmailsImapType;
    inplace: boolean;
    mode?: WidgetDisplayMode;
};

export const officeConfigurationInitialState = {
    username: '',
    password: '',
    hostname: '',
    imapPort: 993,
    imapConnectionSecurity: ImapConnection.NONE,
    imapMailbox: '',
    cardDavAddressBook: '',
    calDavCalendar: '',
    provider: '',
};

export const OfficeConfigurationPrompt = () => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const abortController = useAbortController();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isPending, setIsPending] = useState<boolean>(false);
    const {
        control,
        reset,
        setValue,
        getValues,
        watch,
        formState: { isValid },
    } = useForm<OfficeSetConfigurationPayload>({
        defaultValues: officeConfigurationInitialState,
        criteriaMode: 'all',
    });

    const refresh = () => window.location.reload();
    const [providerList, setProviderList] = useState<OfficeProvider[]>([]);
    const data = watch();

    const imapConnectionSecurityOptions = [
        { label: 'NONE', value: ImapConnection.NONE },
        { label: 'SSL', value: ImapConnection.SSL },
        { label: 'STARTTLS', value: ImapConnection.STARTTLS },
    ];

    const fetchConfiguration = useCallback(
        (providers: string[]) => {
            jApi.office.getConfiguration().then(({ result }) => {
                reset({
                    ...result,
                    provider: !providers.includes(result.provider ?? '') ? providers[0] : result.provider,
                });
            });
        },
        [reset],
    );

    const handleSubmit = () => {
        if (isPending) return;
        setIsPending(true);
        jApi.office
            .setConfiguration(data)
            .then(() => refresh())
            .catch((err) => dispatch(Toast.actions.error({ label: err.errorMsg })))
            .finally(() => {
                setIsOpen(false);
                setIsPending(false);
                reset();
            });
    };

    useEffect(() => {
        if (!isOpen) return;
        setIsPending(true);
        jApi.office
            .getProviders({ signal: abortController.signal })
            .then(({ result }) => {
                if (!abortController.signal.aborted) {
                    setProviderList(result);
                    fetchConfiguration(result.map((provider) => provider.id));
                }
            })
            .catch((err) => {
                if (!abortController.signal.aborted) {
                    dispatch(Toast.actions.error({ label: err.errorMsg }));
                }
            })
            .finally(() => {
                setIsPending(false);
            });
    }, [isOpen, abortController.signal, dispatch, fetchConfiguration]);

    useEffect(() => {
        if (providerList.length === 1) {
            setValue('provider', providerList[0].id);
        }
    }, [providerList, setValue]);

    const handleClose = () => setIsOpen(false);

    const hasProvider = useMemo(() => providerList.length > 1, [providerList]);

    return (
        <>
            <UIWrapper>
                <Button
                    label={intl.formatMessage({ id: 'Office_Set_Configuration' })}
                    onClick={() => setIsOpen(true)}
                />
            </UIWrapper>
            <ModalLayout open={isOpen} radius="small" closeHandler={handleClose}>
                <ModalContent
                    overflow={'scroll'}
                    title={intl.formatMessage({ id: 'Office_Set_Configuration' })}
                    onClickIconClose={handleClose}>
                    {isPending ? (
                        <JRCLoader size="s" />
                    ) : (
                        <>
                            {providerList.length === 0 && (
                                <UIProviderError>{intl.formatMessage({ id: 'Office_No_Providers' })}</UIProviderError>
                            )}
                            <UIForm isLocked={providerList.length === 0}>
                                {hasProvider && (
                                    <>
                                        <UISection>
                                            <Typography size={'l'}>
                                                <FormattedMessage id="OfficeHook_Conf_Provider" />
                                            </Typography>
                                            <SelectProvider
                                                label="OfficeHook_Conf_Select_Provider"
                                                initialValue={{
                                                    label: providerList[0].label,
                                                    value: providerList[0].id,
                                                }}
                                                options={providerList.map((el) => {
                                                    return { label: el.label, value: el.id };
                                                })}
                                                handleChange={(value) => {
                                                    if (getValues('provider') !== value) {
                                                        reset();
                                                    }
                                                    setValue('provider', value);
                                                }}
                                            />
                                        </UISection>
                                    </>
                                )}
                                <UISection>
                                    <Typography size={'l'}>
                                        <FormattedMessage id="OfficeHook_Settings" />
                                    </Typography>
                                    <InputText
                                        control={control}
                                        name={'hostname'}
                                        width={'400'}
                                        placeholder={intl.formatMessage({ id: 'OfficeHook_Conf_IMap_Hostname' })}
                                        rules={{ required: true }}
                                    />
                                    <InputText
                                        control={control}
                                        name={'username'}
                                        width={'400'}
                                        placeholder={intl.formatMessage({ id: 'OfficeHook_Conf_Username' })}
                                        rules={{ required: true }}
                                    />
                                    <InputPassword
                                        control={control}
                                        name={'password'}
                                        width={'400'}
                                        placeholder={intl.formatMessage({ id: 'OfficeHook_Conf_Password' })}
                                        rules={{ required: true }}
                                    />
                                </UISection>
                                <UISection>
                                    <Typography size={'l'}>
                                        <FormattedMessage id="OfficeHook_Conf_IMap_Mailbox" />
                                    </Typography>
                                    <InputNumber
                                        control={control}
                                        name={'imapPort'}
                                        width={'400'}
                                        placeholder={intl.formatMessage({ id: 'OfficeHook_Conf_IMap_Port' })}
                                        rules={{ required: true }}
                                    />
                                    <SelectProvider
                                        label="OfficeHook_Conf_Select_Connection_Security"
                                        initialValue={imapConnectionSecurityOptions[0]}
                                        options={imapConnectionSecurityOptions}
                                        handleChange={(value) =>
                                            setValue('imapConnectionSecurity', value as ValueOf<typeof ImapConnection>)
                                        }
                                    />
                                    <InputText
                                        control={control}
                                        name={'imapMailbox'}
                                        width={'400'}
                                        placeholder={intl.formatMessage({ id: 'OfficeHook_Conf_IMap_Mailbox' })}
                                        rules={{ required: true }}
                                    />
                                </UISection>
                                <UISection>
                                    <Typography size={'l'}>
                                        <FormattedMessage id="OfficeHook_Conf_CalDav_Calendar" />
                                    </Typography>
                                    <InputText
                                        control={control}
                                        name={'calDavCalendar'}
                                        width={'400'}
                                        placeholder={intl.formatMessage({ id: 'OfficeHook_Conf_CalDav_Calendar' })}
                                        rules={{ required: true }}
                                    />
                                </UISection>
                                <UISection>
                                    <Typography size={'l'}>
                                        <FormattedMessage id="OfficeHook_Conf_CardDav_Address_Book" />
                                    </Typography>
                                    <InputText
                                        control={control}
                                        name={'cardDavAddressBook'}
                                        width={'400'}
                                        placeholder={intl.formatMessage({ id: 'OfficeHook_Conf_CardDav_Address_Book' })}
                                        rules={{ required: true }}
                                    />
                                </UISection>
                            </UIForm>
                            <Button
                                label={intl.formatMessage({ id: 'OfficeHook_Conf_Prompt_Submit' })}
                                loader={isPending}
                                disabled={isPending || providerList.length === 0 || !isValid}
                                onClick={handleSubmit}
                            />
                        </>
                    )}
                </ModalContent>
            </ModalLayout>
        </>
    );
};

const SelectProvider = ({
    label,
    initialValue,
    options,
    handleChange,
}: {
    label: string;
    initialValue: { label: string; value: string };
    options: { label: string; value: string }[];
    handleChange: (value: string) => void;
}) => {
    const { control, watch } = useForm<{ provider: { label: string; value: string } }>({
        defaultValues: {
            provider: initialValue,
        },
        criteriaMode: 'all',
    });

    useEffect(() => {
        const subscription = watch((data) => {
            if (!data.provider || !data.provider.value) return;
            handleChange(data.provider.value);
        });
        return () => subscription.unsubscribe();
    }, [watch, handleChange]);

    return (
        <InputSelectExtended
            label={label}
            name="provider"
            options={options ?? []}
            getValue={(o) => o.value}
            getLabel="label"
            getSearch="label"
            control={control}
            rules={{ required: true }}
        />
    );
};
